import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import api from '../../services/api';
import SABadge from '../../components/SuperAdmin/SABadge';
import '../../assets/styles/SuperAdmin/SATheme.css';
import '../../assets/styles/SuperAdmin/SACredentials.css';

/* ─── Inline Toast inside Modal ─────────────────────────────────────── */
const InlineMsg = ({ msg, type }) => {
    if (!msg) return null;
    const bg = type === 'error' ? '#ff5252' : '#2563eb';
    return (
        <div style={{
            background: bg + '18', border: `1px solid ${bg}40`,
            color: bg, borderRadius: 8, padding: '8px 14px',
            fontSize: 12, fontWeight: 600, marginBottom: 14
        }}>{msg}</div>
    );
};

/* ─── Portal wrapper ─────────────────────────────────────────────────── */
const CredModal = ({ onClose, children }) => ReactDOM.createPortal(
    <div
        onClick={onClose}
        style={{
            position: 'fixed', inset: 0,
            background: 'rgba(6,8,12,0.88)',
            backdropFilter: 'blur(8px)',
            zIndex: 99999,
            display: 'flex', alignItems: 'center', justifyContent: 'center'
        }}
    >
        <div
            onClick={e => e.stopPropagation()}
            style={{
                background: 'var(--sa-card)',
                border: '1px solid var(--sa-border)',
                borderRadius: 18, padding: 28,
                width: 480, maxWidth: '94vw',
                color: 'var(--sa-text)',
                boxShadow: '0 32px 100px rgba(0,0,0,0.7)'
            }}
        >
            {children}
        </div>
    </div>,
    document.body
);

/* ─── Add Credential Modal ───────────────────────────────────────────── */
const AddCredModal = ({ onClose, onSuccess }) => {
    const [form, setForm] = useState({ name: '', role: 'hr', email: '', id: '', password: '' });
    const [loading, setLoading] = useState(false);
    const [msg, setMsg] = useState(null);

    const handleCreate = async () => {
        if (!form.name.trim()) return setMsg({ text: 'Full name is required', type: 'error' });
        if (!form.id.trim()) return setMsg({ text: 'Login ID is required', type: 'error' });
        if (!form.id.trim()) return setMsg({ text: 'Email ID is required', type: 'error' });
        if (!form.password.trim()) return setMsg({ text: 'Password is required', type: 'error' });

        setLoading(true);
        setMsg(null);
        try {
            await api.createUser({
                empId: form.id.trim(),
                name: form.name.trim(),
                email: form.email.trim() || form.id.trim().toLowerCase().replace(/[^a-z0-9]/g, '') + '@amshrms.in',
                role: form.role,
                password: form.password.trim()
            });
            setMsg({ text: '✅ Credential created successfully!', type: 'success' });
            setTimeout(() => { onSuccess(); onClose(); }, 1000);
        } catch (e) {
            setMsg({ text: e.message || 'Failed to create credential', type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    return (
        <CredModal onClose={onClose}>
            <div style={{ fontSize: 18, fontWeight: 800, marginBottom: 6, fontFamily: "'Sora'" }}>Add Credential</div>
            <div style={{ fontSize: 12, color: 'var(--sa-muted)', marginBottom: 20 }}>Create login access for HR Admin, Project Manager, or IT Admin</div>

            <InlineMsg msg={msg?.text} type={msg?.type} />

            <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--sa-muted)', marginBottom: 4 }}>FULL NAME *</div>
                <input
                    autoFocus
                    value={form.name}
                    onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                    placeholder="e.g. Priya K."
                    className="sa-cred-input"
                />
            </div>

            <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--sa-muted)', marginBottom: 4 }}>ROLE *</div>
                <select
                    value={form.role}
                    onChange={e => setForm(f => ({ ...f, role: e.target.value }))}
                    className="sa-cred-input"
                    style={{ cursor: 'pointer' }}
                >
                    <option value="hr">HR Admin</option>
                    <option value="pm">Project Manager</option>
                    <option value="itadmin">IT Admin</option>
                </select>
            </div>

            <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--sa-muted)', marginBottom: 4 }}>EMAIL *</div>
                <input
                    value={form.email}
                    onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                    placeholder="e.g. priya@amshrms.in"
                    className="sa-cred-input"
                    type="email"
                />
            </div>

            <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--sa-muted)', marginBottom: 4 }}>LOGIN ID *</div>
                <input
                    value={form.id}
                    onChange={e => setForm(f => ({ ...f, id: e.target.value }))}
                    placeholder="e.g. IT-2026-0001"
                    className="sa-cred-input"
                />
            </div>

            <div style={{ marginBottom: 24 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--sa-muted)', marginBottom: 4 }}>PASSWORD *</div>
                <input
                    value={form.password}
                    onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                    placeholder="e.g. it@456"
                    className="sa-cred-input"
                    type="text"
                />
            </div>

            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                <button onClick={onClose} className="sa-action-btn" disabled={loading}>Cancel</button>
                <button
                    onClick={handleCreate}
                    disabled={loading}
                    style={{
                        background: loading ? 'var(--sa-dim)' : 'var(--sa-accent)',
                        color: '#fff', border: 'none',
                        padding: '9px 24px', borderRadius: 8,
                        fontWeight: 700, cursor: loading ? 'not-allowed' : 'pointer',
                        fontSize: 13, transition: 'all 0.2s'
                    }}
                >
                    {loading ? 'Creating...' : 'Create'}
                </button>
            </div>
        </CredModal>
    );
};

/* ─── Edit Credential Modal ──────────────────────────────────────────── */
const EditCredModal = ({ cred, onClose, onSuccess }) => {
    const [form, setForm] = useState({ name: cred.name, password: '' });
    const [loading, setLoading] = useState(false);
    const [msg, setMsg] = useState(null);

    const handleSave = async () => {
        if (!form.password.trim()) return setMsg({ text: 'New password is required', type: 'error' });
        setLoading(true);
        setMsg(null);
        try {
            await api.resetUserPassword(cred.emp_id, form.password.trim());
            setMsg({ text: '✅ Password updated!', type: 'success' });
            setTimeout(() => { onSuccess(); onClose(); }, 1000);
        } catch (e) {
            setMsg({ text: e.message || 'Failed to update password', type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    return (
        <CredModal onClose={onClose}>
            <div style={{ fontSize: 18, fontWeight: 800, marginBottom: 6, fontFamily: "'Sora'" }}>Edit Credential</div>
            <div style={{ fontSize: 12, color: 'var(--sa-muted)', marginBottom: 20 }}>
                Editing: <strong style={{ color: 'var(--sa-text)' }}>{cred.name}</strong>
                <code style={{ background: 'var(--sa-bg)', padding: '2px 6px', borderRadius: 4, color: 'var(--sa-orange)', fontSize: 11, marginLeft: 8 }}>{cred.id}</code>
            </div>

            <InlineMsg msg={msg?.text} type={msg?.type} />

            <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--sa-muted)', marginBottom: 4 }}>NAME</div>
                <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} className="sa-cred-input" />
            </div>

            <div style={{ marginBottom: 24 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--sa-muted)', marginBottom: 4 }}>NEW PASSWORD *</div>
                <input
                    value={form.password}
                    onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                    placeholder="Enter new password"
                    className="sa-cred-input"
                    type="text"
                    autoFocus
                />
            </div>

            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                <button onClick={onClose} className="sa-action-btn" disabled={loading}>Cancel</button>
                <button
                    onClick={handleSave}
                    disabled={loading}
                    style={{
                        background: loading ? 'var(--sa-dim)' : 'var(--sa-accent)',
                        color: '#fff', border: 'none',
                        padding: '9px 24px', borderRadius: 8,
                        fontWeight: 700, cursor: loading ? 'not-allowed' : 'pointer',
                        fontSize: 13
                    }}
                >
                    {loading ? 'Saving...' : 'Save'}
                </button>
            </div>
        </CredModal>
    );
};

/* ─── Main Component ─────────────────────────────────────────────────── */
const SACredentials = ({ saCreds, onRefresh }) => {
    const [visibleIds, setVisibleIds] = useState({});
    const [showAdd, setShowAdd] = useState(false);
    const [editCred, setEditCred] = useState(null);

    const toggleVisible = (id) => setVisibleIds(prev => ({ ...prev, [id]: !prev[id] }));

    return (
        <div className="animate-in">
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
                <div>
                    <h2 style={{ margin: 0, fontSize: 20, fontWeight: 800, fontFamily: "'Sora'" }}>Credentials Management</h2>
                    <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--sa-muted)' }}>Manage login credentials for HR Admin, Project Manager &amp; IT Admin</p>
                </div>
                <button
                    onClick={() => setShowAdd(true)}
                    style={{ background: 'var(--sa-accent)', color: '#fff', border: 'none', padding: '8px 20px', borderRadius: 8, fontWeight: 700, fontSize: 12, cursor: 'pointer', fontFamily: "'DM Sans'" }}
                >
                    + Add Credential
                </button>
            </div>

            {/* Stat Cards */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 20, marginBottom: 20 }}>
                {[
                    { label: 'HR Admin', role: 'hr', color: 'var(--sa-teal)' },
                    { label: 'Project Manager', role: 'pm', color: 'var(--sa-purple)' },
                    { label: 'IT Admin', role: 'itadmin', color: 'var(--sa-orange)' },
                ].map(({ label, role, color }) => (
                    <div key={role} className="sa-cred-card">
                        <div style={{ fontSize: 15, fontWeight: 800, marginBottom: 4 }}>{label}</div>
                        <div style={{ fontSize: 28, fontWeight: 800, color }}>{saCreds.filter(c => c.role === role).length}</div>
                        <div style={{ fontSize: 11, color: 'var(--sa-muted)' }}>credentials</div>
                    </div>
                ))}
            </div>

            {/* Table */}
            <div className="sa-user-table-card">
                <table className="sa-table">
                    <thead>
                        <tr>
                            <th>Name</th><th>Login ID</th><th>Password</th><th>Role</th><th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {saCreds.length === 0 && (
                            <tr>
                                <td colSpan={5} style={{ textAlign: 'center', color: 'var(--sa-muted)', padding: '32px 0', fontSize: 13 }}>
                                    No credentials yet — click <strong>+ Add Credential</strong> to get started.
                                </td>
                            </tr>
                        )}
                        {saCreds.map((c, i) => (
                            <tr key={i}>
                                <td style={{ fontWeight: 700 }}>{c.name}</td>
                                <td><code style={{ background: 'var(--sa-bg)', padding: '2px 6px', borderRadius: 4, color: 'var(--sa-orange)', fontSize: 11 }}>{c.id}</code></td>
                                <td>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                        <span style={{ fontSize: 12, fontFamily: 'monospace', color: visibleIds[c.id] ? 'var(--sa-text)' : 'var(--sa-dim)' }}>
                                            {visibleIds[c.id] ? (c.password || '—') : '••••••••'}
                                        </span>
                                        <button onClick={() => toggleVisible(c.id)} style={{ background: 'none', border: '1px solid var(--sa-border)', borderRadius: 5, padding: '2px 8px', fontSize: 9, color: 'var(--sa-muted)', cursor: 'pointer' }}>
                                            {visibleIds[c.id] ? 'Hide' : 'Show'}
                                        </button>
                                    </div>
                                </td>
                                <td>
                                    {c.role === 'hr' && <SABadge color="teal">HR Admin</SABadge>}
                                    {c.role === 'pm' && <SABadge color="purple">Project Mgr</SABadge>}
                                    {c.role === 'itadmin' && <SABadge color="orange">IT Admin</SABadge>}
                                    {!['hr', 'pm', 'itadmin'].includes(c.role) && <SABadge color="blue">{c.role}</SABadge>}
                                </td>
                                <td>
                                    <div style={{ display: 'flex', gap: 6 }}>
                                        <button onClick={() => setEditCred(c)} className="sa-action-btn" style={{ color: 'var(--sa-blue)', borderColor: 'rgba(77,168,255,0.2)' }}>Edit</button>
                                        <button onClick={async () => { if (window.confirm(`Delete ${c.name} (${c.id})? This cannot be undone.`)) { try { await api.deleteUser(c.emp_id || c.id); onRefresh(); } catch (e) { alert(e.message || 'Failed to delete'); } } }} className="sa-action-btn" style={{ color: 'var(--sa-red)', borderColor: 'rgba(255,107,107,0.2)' }}>Delete</button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Add Modal */}
            {showAdd && (
                <AddCredModal
                    onClose={() => setShowAdd(false)}
                    onSuccess={onRefresh}
                />
            )}

            {/* Edit Modal */}
            {editCred && (
                <EditCredModal
                    cred={editCred}
                    onClose={() => setEditCred(null)}
                    onSuccess={onRefresh}
                />
            )}
        </div>
    );
};

export default SACredentials;
